import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Badge, Button, Card, Checkbox } from '@/components/ui'
import { Info, Plus, Trash } from 'lucide-react'
import { CreateEditModal } from './CreateEditModal'
import { SegmentModal } from './SegmentModal'
import { INotification, Notifications } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import {
  deleteNotification,
  getAllNotifications,
  getNotification,
} from '../../../core/services/notifications'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Icon } from '@/common/components/Icon'

export const NotificationPage: React.FC = () => {
  const { sortBy, setSortBy } = useTableParams()

  const [notifications, setNotifications] = useState<Notifications[]>([])
  const [showSegment, setShowSegment] = useState<boolean>(false)
  const [selectedItem, setSelectedItem] = useState<INotification>()
  const [showCreateNotification, setShowCreateNotification] =
    useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { festivalName, editionId } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      if (sortDirection === 'ASC') {
        setSortDirection('DESC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('ASC')
      setSortBy(sort)
    }
  }

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getAllNotifications(editionId || '')
        if (response.status === 200) {
          setNotifications(response.data)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const deleteFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        const deleteStatuses = await Promise.all(
          isCheck.map(async (check) => {
            const { status } = await deleteNotification(check)
            return status
          }),
        )
        const response = await getAllNotifications(editionId || '')
        if (deleteStatuses.every((d) => d === 200) && response.status === 200) {
          setNotifications(response.data)
          setIsCheck([])
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const handleSelectAll = () => {
    const nextChecked = !isCheckAll
    setIsCheckAll(nextChecked)
    setIsCheck(nextChecked ? notifications.map((n) => n.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleActivateSegment = async (id: string) => {
    try {
      // TODO link to api when we have
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
      width: '16px',
      sortable: false,
    },
    {
      title: 'Campaign',
      accessor: '',
      render: (row) => (
        <div className="flex flex-col gap-1">
          <span>{row.title}</span>
          <span>{row.text}</span>
        </div>
      ),
    },
    {
      title: 'Start',
      accessor: 'time',
      render: (row: Notifications) => <span>{formatDateTime(row.time)}</span>,
    },
    {
      title: 'Status',
      accessor: 'status',
    },
    {
      title: 'Target',
      accessor: '',
      render: (row: Notifications) => (
        <div className="flex gap-1">
          {row.forAndroid && (
            <Badge variant="secondary" className="text-xs text-white">
              Android
            </Badge>
          )}
          {row.forIOS && (
            <Badge variant="secondary" className="text-xs text-white">
              iOS
            </Badge>
          )}
        </div>
      ),
    },
    {
      title: 'Sends',
      accessor: 'sends',
    },
    {
      title: 'Clicks',
      accessor: 'clicks',
    },
    {
      title: 'Segments',
      accessor: '',
      render: (row) => {
        if (row.activeSegments) {
          return (
            <div
              className="flex items-center gap-2"
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                const resp = await getNotification(row.id)
                if (resp.status === 200) {
                  setSelectedItem(resp.data)
                  setShowSegment(true)
                }
              }}
            >
              <Info className="h-4 w-4 text-accent-1" />
            </div>
          )
        } else {
          return (
            <Checkbox
              onCheckedChange={() => {
                handleActivateSegment(row.id)
              }}
            />
          )
        }
      },
    },
  ]

  const handleDelete = () => {
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  return (
    <>
      <Header
        pageTitle="Notifications"
        actions={
          <>
            {isCheck.length >= 1 && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            <Button
              variant="primary-bordered"
              onClick={() => {
                setShowCreateNotification(true)
              }}
            >
              Add New
              <Icon
                icon="plus-circle"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              />
            </Button>
          </>
        }
      />
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Notifications" className="mb-5" />
        <Table
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={notifications}
        />
      </Card>
      <CreateEditModal
        setVisible={setShowCreateNotification}
        visible={showCreateNotification}
        setNotifications={setNotifications}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Notification'}
        deleteFunc={deleteFunc}
      />
      <SegmentModal
        visible={showSegment}
        setVisible={setShowSegment}
        selectedItem={selectedItem}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
